import axios from 'axios'
import { message } from 'antd'
import { toast } from 'react-toastify'
import { ROUTES } from '@/constants'
import { API_BASE_URL } from '@/constants'

// Create axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
})

// Store reference for accessing current state
let store = null

// Track refresh token state to prevent infinite loops
let isRefreshing = false
let failedQueue = []

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error)
        } else {
            // Update the request config with new token and retry
            prom.config.headers.Authorization = `Bearer ${token}`
            prom.resolve(apiClient(prom.config))
        }
    })
    failedQueue = []
}

// Inject store for accessing state in interceptors
export const injectStore = (storeInstance) => {
    store = storeInstance
}

// Request interceptor - add auth token
apiClient.interceptors.request.use(
    (config) => {
        // Get token from store if available
        if (store) {
            const state = store.getState()
            const token = state.auth?.token || state.auth?.accessToken

            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor - handle errors and token refresh
apiClient.interceptors.response.use(
    (response) => {
        return response
    },
    async (error) => {
        const originalRequest = error.config
        const { response } = error

        // Handle 401 Unauthorized - try to refresh token
        if (response?.status === 401 && !originalRequest._retry) {
            // Skip refresh for authentication endpoints (login, register, refresh)
            // These endpoints should handle their own errors without trying to refresh token
            const authEndpoints = ['/auth/login', '/auth/register', '/auth/refresh']
            const isAuthEndpoint = authEndpoints.some(endpoint =>
                originalRequest.url?.includes(endpoint)
            )

            if (isAuthEndpoint) {
                // For auth endpoints, just reject the error without trying to refresh
                // Let the mutation hooks handle the error messages
                return Promise.reject(error)
            }

            // Check if we have a refresh token before attempting refresh
            // If no store or no refresh token, just reject the error
            if (!store) {
                return Promise.reject(error)
            }

            const state = store.getState()
            const refreshToken = state.auth?.refreshToken

            // If no refresh token, don't try to refresh - just reject the error
            if (!refreshToken) {
                return Promise.reject(error)
            }

            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject, config: originalRequest })
                })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {

                // Create a new axios instance without interceptors for refresh call
                const refreshClient = axios.create({
                    baseURL: API_BASE_URL,
                    timeout: 10000,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })

                // Call refresh token endpoint
                const refreshResponse = await refreshClient.post(`${API_BASE_URL}/auth/refresh`, {
                    refreshToken
                })

                if (refreshResponse.data?.success && refreshResponse.data?.data) {
                    const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data.data

                    // Update tokens in store
                    const { updateTokens } = await import('@/store/slices/authSlice.js')
                    store.dispatch(updateTokens({
                        accessToken,
                        refreshToken: newRefreshToken
                    }))

                    // Update localStorage
                    const authData = JSON.parse(localStorage.getItem('auth_data') || '{}')
                    if (authData) {
                        authData.accessToken = accessToken
                        authData.refreshToken = newRefreshToken
                        localStorage.setItem('auth_data', JSON.stringify(authData))
                    }

                    // Update original request with new token
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`

                    // Process queued requests
                    processQueue(null, accessToken)

                    // Retry original request
                    return apiClient(originalRequest)
                } else {
                    throw new Error('Invalid refresh response')
                }
            } catch (refreshError) {
                // Refresh failed, logout and redirect
                processQueue(refreshError, null)
                if (store) {
                    const { logout } = await import('@/store/slices/authSlice.js')
                    store.dispatch(logout())
                }
                toast.error('Your session has expired. Please log in again.')
                window.location.href = ROUTES.LOGIN
                return Promise.reject(refreshError)
            } finally {
                isRefreshing = false
            }
        }

        // Handle other errors
        if (response) {
            const { status, data } = response

            switch (status) {
                case 403:
                    // Forbidden - insufficient permissions
                    toast.error('You do not have permission to access this resource')
                    break

                case 404:
                    // Not found
                    toast.error('Resource not found')
                    break

                case 422:
                    // Validation error
                    if (data?.errors) {
                        const errorMessages = Object.values(data.errors).flat()
                        errorMessages.forEach(msg => message.error(msg))
                    } else {
                        toast.error('Invalid data')
                    }
                    break

                case 500:
                    // Server error
                    toast.error('Server error. Please try again later')
                    break

                default:
                    toast.error(data?.message || 'An unknown error occurred')
            }
        } else if (error.request) {
            // Network error
            toast.error('Unable to connect to server')
        } else {
            // Other error
            toast.error('An unknown error occurred')
        }

        return Promise.reject(error)
    }
)

// Helper functions for common apiClient methods
export default apiClient
